/**
 * A PRESENTATIONAL COPY of the event modal a carer opens from the approval card
 * in MyPymble, for the editor's "Preview permission card".
 *
 * Source: my-pymble-portal, src/components/ (read only, at portal HEAD on
 * feature/approvals-actions-in-modal, 2026-10-23, 5e03abc91):
 *   approvals/EventItemDetail.tsx (+ .css), approvals/ApprovalItem.tsx (+ .css),
 *   approvals/ApprovalDietary.tsx (+ .css), approvals/DisabledReasonHint.tsx,
 *   approvals/approvalDisabledReason.ts,
 *   approvals/EventTabs.tsx (+ .css), approvals/EventItemExternalWarning.tsx
 *   (+ .css), calendar/EventItemModal.css, common/TimeShort.
 * The Detail tab only, in the portal's order: header, meta row (thumbnail,
 * owner, date, time, location), description then the full Additional Notes, then
 * the approval block. The modal is where a carer decides (the card is only a
 * summary): medical, terms, travel acknowledgement and the dietary link, then
 * the lock-window warning, the external-form notice and the buttons.
 *
 * The rows are the card's own (see ApprovalCardPreview), so wording and markup
 * come from one place. The student is the same invented "Sample Student".
 *
 * What is NOT drawn, on purpose: add to calendar, the close button (the window
 * has its own), the Photos tab, the notify-coach button, weather, map and
 * directions, related notices, the owner's and student's photos, the decided
 * state, and the confirm dialogs and toasts. Nothing here submits or fetches,
 * so every control is inert.
 *
 * Differences from the portal that cannot be closed from here:
 *  - The portal draws the medical row only when the approval carries
 *    `Urls.MedicalAuth.Link`, which the draft has no field for. It is drawn
 *    disabled whenever the medical check is on and there is no external form,
 *    as the card preview does.
 *  - The portal falls back to a default notice image when the event has no
 *    banner; its URL is not available here, so the thumbnail is left out.
 *  - The dietary link opens the portal's entry modal (DietaryPreviewModal);
 *    here the text is kept in state only, cleared on close, and Save just
 *    closes, since nothing is saved from a preview.
 *  - Compulsory hides only Decline; Approve stays.
 *
 * Copy, not import: drift is possible. When the portal modal changes, re-read
 * the files above and update this one.
 */
import React, { useState } from "react";
import {
	IonButton,
	IonCol,
	IonGrid,
	IonIcon,
	IonItem,
	IonRow,
	IonText,
} from "@ionic/react";
import {
	arrowForwardOutline,
	calendarClearOutline,
	checkmark,
	close,
	locationOutline,
	medicalOutline,
	notifications,
	timeOutline,
} from "ionicons/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons";
import { CCRichText } from "@pymblecitizencoders/cc-ui/editor";
import { EventDoc } from "../../types/event";
import {
	APPROVAL_TEXT as TEXT,
	SAMPLE_STUDENT,
	buildEventModalModel,
} from "../../utils/permissionCard";
import { supervisorNames } from "../../utils/eventOwner";
import { asHtml } from "../../utils/richText";
import {
	MedicalRow,
	TermsModal,
	TermsRow,
	TransportAckRow,
} from "./ApprovalCardPreview";
import { DietaryPreviewModal } from "./DietaryPreviewModal";
import "./approvalCardPreview.css";

/* The portal's tab labels (EventTabs `Tab` enum) and the notes heading
   (`event.additional-notes`, default in EventItemDetail). */
const DETAIL_TAB = "Event Details";
const NOTES_HEADING = "Additional Notes";

interface EventModalPreviewProps {
	/** The editor's draft, so unsaved edits are what gets drawn. */
	event: EventDoc;
}

export const EventModalPreview: React.FC<EventModalPreviewProps> = ({
	event,
}) => {
	const [showTerms, setShowTerms] = useState(false);
	const [showDietary, setShowDietary] = useState(false);
	/* The roll on a single read, `Supervisors` on a list row; see eventOwner. */
	const fromRoll = supervisorNames(event.Attendees);
	const owners = fromRoll.length ? fromRoll : supervisorNames(event.Supervisors);
	const m = buildEventModalModel(event, new Date(), owners.join(", ") || null);

	/* Owner, date and time rows, plus a location row, as the portal counts them;
	   the thumbnail is as tall as the rows beside it. */
	const rows =
		(m.ownerName ? 1 : 0) +
		(m.dateLabel ? 1 : 0) +
		(m.timeLabel ? 1 : 0) +
		(m.locationLabel ? 1 : 0);

	return (
		<div className="em-event-modal-preview">
			<header className="em-evm-header">
				<div className="em-evm-student">
					<FontAwesomeIcon
						icon={faCircleUser}
						className="em-evm-avatar"
						aria-hidden="true"
					/>
					<IonText>{SAMPLE_STUDENT}</IonText>
				</div>
				<h3 className="em-evm-title">{m.title}</h3>
			</header>

			<div className="em-evm-tabs">
				<span className="em-evm-tab">{DETAIL_TAB}</span>
			</div>

			<div className="em-evm-meta">
				{m.imageUrl && (
					<div
						className="em-evm-thumb"
						style={{ "--thumb-rows": Math.max(rows, 2) } as React.CSSProperties}
					>
						<img src={m.imageUrl} alt="" loading="lazy" />
					</div>
				)}
				<div className="em-evm-meta-rows">
					{m.ownerName && (
						<div className="em-evm-row">
							<IonText>{m.ownerName}</IonText>
						</div>
					)}
					{m.dateLabel && (
						<div className="em-evm-row">
							<IonIcon icon={calendarClearOutline} size="small" />
							<span>{m.dateLabel}</span>
						</div>
					)}
					{m.timeLabel && (
						<div className="em-evm-row">
							<IonIcon icon={timeOutline} size="small" />
							<span>{m.timeLabel}</span>
						</div>
					)}
					{m.locationLabel && (
						<div className="em-evm-row">
							<IonIcon icon={locationOutline} size="small" />
							<span>{m.locationLabel}</span>
						</div>
					)}
				</div>
			</div>

			{(m.bodyHtml || m.additionalNotesHtml) && (
				<div className="em-evm-descriptor" role="region" aria-label="Event description">
					{m.bodyHtml && (
						<div className="em-evm-block">
							<CCRichText value={asHtml(m.bodyHtml)} editable={false} />
						</div>
					)}
					{m.additionalNotesHtml && (
						<div className="em-evm-block">
							<div className="em-evm-notes-title">{NOTES_HEADING}</div>
							<CCRichText value={asHtml(m.additionalNotesHtml)} editable={false} />
						</div>
					)}
				</div>
			)}

			{/* The approval block borrows the card's scope for its row styling. */}
			<div className="em-approval-preview em-evm-approval">
				{m.externalMedical && (
					<IonItem lines="none" className="external-warning-item">
						<IonIcon
							aria-hidden="true"
							slot="start"
							icon={notifications}
							className="warning-icon"
						/>
						{TEXT.externalNotice}
					</IonItem>
				)}

				<IonRow className="no-border approval-checks-row">
					{m.medicalRow && (
						<IonCol size="12">
							<MedicalRow />
						</IonCol>
					)}
					{m.termsHtml && (
						<IonCol size="12">
							<TermsRow onView={() => setShowTerms(true)} />
						</IonCol>
					)}
					{m.transportAck && (
						<IonCol size="12">
							<TransportAckRow text={m.transportAck.text} />
						</IonCol>
					)}
					{m.dietary && !m.past && (
						<IonCol size="12">
							<div className="approval-dietary">
								<button
									type="button"
									className="approval-dietary-link"
									onClick={() => setShowDietary(true)}
								>
									{TEXT.dietaryQuestion}
								</button>
							</div>
						</IonCol>
					)}
				</IonRow>

				{!m.past && (
					<IonGrid>
						{m.expiryWarning && (
							<IonRow className="no-border">
								<IonCol>
									<IonText
										className="ion-text-center approval-item-warning"
										color="danger"
									>
										<p>
											<strong>
												{TEXT.expiryWarning(m.lockDays ?? 0)}
												<br />
												{TEXT.contactCollege}
											</strong>
										</p>
									</IonText>
								</IonCol>
							</IonRow>
						)}
						{m.externalPermission && (
							<IonRow className="no-border">
								<IonCol>{TEXT.externalNotice}</IonCol>
							</IonRow>
						)}
						<IonRow className="no-border approval-buttons">
							{m.externalPermission ? (
								<>
									{m.externalMedical && (
										<IonButton
											fill="solid"
											color="danger"
											className="ion-text-capitalize"
											disabled
										>
											<IonIcon icon={medicalOutline} slot="start" size="small" />
											{TEXT.medicalInfo}
											<IonIcon icon={arrowForwardOutline} slot="end" size="small" />
										</IonButton>
									)}
									<IonButton
										fill="solid"
										color="success"
										className="ion-text-capitalize"
										disabled
									>
										{TEXT.approveNow}
										<IonIcon icon={arrowForwardOutline} slot="end" size="small" />
									</IonButton>
								</>
							) : (
								<>
									<span
										className="approval-button-hint"
										title={m.disabledReason || undefined}
									>
										<IonButton
											fill="solid"
											color="success"
											className="ion-text-capitalize"
											disabled
										>
											{TEXT.approve}
											<IonIcon icon={checkmark} slot="end" size="small" />
										</IonButton>
									</span>
									{m.showDecline && (
										<span
											className="approval-button-hint"
											title={m.declineDisabledReason || undefined}
										>
											<IonButton
												fill="solid"
												color="danger"
												className="ion-text-capitalize"
												disabled
											>
												{TEXT.decline}
												<IonIcon icon={close} slot="end" size="small" />
											</IonButton>
										</span>
									)}
								</>
							)}
						</IonRow>
					</IonGrid>
				)}
			</div>

			<TermsModal
				open={showTerms}
				onClose={() => setShowTerms(false)}
				termsHtml={m.termsHtml}
			/>
			<DietaryPreviewModal
				open={showDietary}
				onClose={() => setShowDietary(false)}
			/>
		</div>
	);
};
